import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import {AddOutlined, DeleteOutlined, GetAppOutlined, PublishOutlined} from '@material-ui/icons';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import OutlinedButton from '../../../shared/buttons/OutlinedButton.tsx';
import useConfirmationDialog from '../../../shared/confirmationdialog/useConfirmationDialog.js';
import {useToast} from '../../../shared/toast/ToastProvider.tsx';
import useAlertMessage from '../../../../hooks/useAlertMessage.js';
import {buttonTableCell} from '../../../../utils/styles.js';
import {criarColunaOpcoes} from '../../components/ColunaOpcoes.jsx';
import {ouTraco} from '../../components/inscricao/documentoInscricao.js';
import {CampoSimNao} from '../../components/processo/CamposProcesso.jsx';
import {filtroCpf, formatarCpf, formatarData, mesclarCargas} from '../../components/processo/processoSelecao.js';
import DialogCargaCandidato from '../../dialogs/processo/DialogCargaCandidato.jsx';
import DialogImportarCarga from '../../dialogs/processo/DialogImportarCarga.jsx';
import {URL_MODELO_CARGA_PROCESSO_SELECAO} from '../../../../constants/index.js';

const TABLE_CONTAINER_PROPS = {component: 'div'};
const FILTER_OPTIONS = {filterByColumn: true};

/* Mesmos callbacks no-op da PAGINACAO_PADRAO (components/paginacao.js), com 10 linhas como a carga do JSF. */
const PAGINACAO_CARGA = {
    rowsPerPage: 10,
    startPage: 0,
    resetPageOnRowsChange: true,
    onChangePage: () => {},
    onChangeRowsPerPage: () => {}
};

const useStyles = makeStyles(theme => ({
    botoes: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1)
    },
    ...buttonTableCell(theme)
}));

/*
 * Aba "Carga": candidatos aceitos no processo de seleção. Podem vir da
 * planilha (modelo para baixar e importação) ou ser adicionados um a um; CPF
 * repetido não entra. "Aceitar inscrição somente de candidatos da carga"
 * restringe a inscrição pública a esta lista.
 */
export default function TabCarga({control, errors, watch, setValue}) {
    const classes = useStyles();
    const confirmation = useConfirmationDialog();
    const {createToast} = useToast();
    const showAlertMessage = useAlertMessage();
    const [dialogCandidatoAberto, setDialogCandidatoAberto] = useState(false);
    const [dialogImportarAberto, setDialogImportarAberto] = useState(false);

    const cargas = watch('cargas') || [];

    const gravar = lista => setValue('cargas', lista, {shouldDirty: true});

    const adicionar = candidato => gravar([...cargas, candidato]);

    const importar = lidas => {
        const {cargas: novas, adicionadas, duplicados} = mesclarCargas(cargas, lidas);
        gravar(novas);
        createToast({severity: 'success', message: `${adicionadas} candidato(s) importado(s).`});
        // CPFs repetidos ficam de fora; a lista pode ser longa, por isso vai num aviso que o usuário fecha.
        if (duplicados.length) {
            showAlertMessage(`${duplicados.length} candidato(s) não importado(s) porque o CPF já estava informado nesta carga: ${duplicados.join(', ')}.`);
        }
    };

    const remover = posicao => {
        confirmation('Tem certeza que deseja remover esse candidato da carga?', () =>
            gravar(cargas.filter((item, atual) => atual !== posicao)));
    };

    return (
        <>
            <Grid container spacing={3}>
                <CampoSimNao control={control} errors={errors} nome='flagInscricaoCargaPropria'
                             rotulo='Aceitar inscrição somente de candidatos da carga *' sm={12}/>
                <Grid item xs={12}>
                    <DataGrid
                        toolbarContent={(
                            <div className={classes.botoes}>
                                <OutlinedButton startIcon={<GetAppOutlined/>} component='a' href={URL_MODELO_CARGA_PROCESSO_SELECAO} download>
                                    Baixar modelo
                                </OutlinedButton>
                                <OutlinedButton startIcon={<PublishOutlined/>} onClick={() => setDialogImportarAberto(true)}>
                                    Importar Excel
                                </OutlinedButton>
                                <ContainedButton startIcon={<AddOutlined/>} onClick={() => setDialogCandidatoAberto(true)}>
                                    Adicionar
                                </ContainedButton>
                            </div>
                        )}
                        tableContainerProps={TABLE_CONTAINER_PROPS}
                        rows={cargas}
                        filterOptions={FILTER_OPTIONS}
                        paginate
                        paginateOptions={PAGINACAO_CARGA}
                        noRecordsText='Nenhum candidato na carga.'
                        columns={[
                            {label: 'Nome', field: 'nome', filterable: true, valueGetter: valor => ouTraco(valor)},
                            {
                                label: 'CPF', field: 'cpf', filterable: true,
                                valueGetter: valor => ouTraco(formatarCpf(valor)),
                                customFilter: (termo, valor) => filtroCpf(termo, valor)
                            },
                            {label: 'Cód. Inscrito', field: 'codigoInscrito', valueGetter: valor => ouTraco(valor)},
                            {label: 'Data Nasc.', field: 'dataNascimento', valueGetter: valor => formatarData(valor)},
                            criarColunaOpcoes(row => {
                                const posicao = cargas.indexOf(row);
                                return [{titulo: 'Excluir', icone: <DeleteOutlined/>, onClick: () => remover(posicao)}];
                            }, {className: classes.buttonTableCell})
                        ]}/>
                </Grid>
            </Grid>
            <DialogCargaCandidato
                open={dialogCandidatoAberto}
                onClose={() => setDialogCandidatoAberto(false)}
                cargas={cargas}
                onSalvar={adicionar}/>
            <DialogImportarCarga
                open={dialogImportarAberto}
                onClose={() => setDialogImportarAberto(false)}
                onImportar={importar}/>
        </>
    );
}

TabCarga.propTypes = {
    control: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    watch: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired
};
